import { AlertCircleIcon } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { getSurveyAccessoryViewId, SurveyKeyboardDoneBar } from './SurveyKeyboardDoneBar';
import { SurveyQuestionLabel } from './SurveyQuestionLabel';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { getSurveyProseKeyboardProps } from './surveyKeyboard';
import type { SurveyElement } from '../../model/surveySchema';

/**
 * The primitive ships a fixed `h-[100px]`, which the parent lays the field out
 * at no matter what height an inline style paints it. Left in place, a taller
 * textarea overflows its own form control by the difference and collides with
 * whatever follows it — visible wherever the next sibling's gap is smaller than
 * that difference. Clearing the class is what lets the height below govern the
 * layout the parent measures, so the two cannot disagree.
 */
const TEXTAREA_HEIGHT_RESET = 'h-auto';

/** The brand border marks the control boundary; `primary-500` clears 3:1 on white in both themes. */
const EMPHASIZED_TEXTAREA_FRAME =
  'border-primary-500 bg-background-0 data-[hover=true]:border-primary-600';

/** The default placeholder is `typography-500`, which is large-text-only on white. */
const EMPHASIZED_PLACEHOLDER = 'placeholder:text-typography-600';

/** Rough line height used to turn the schema's `rows` into a pixel height. */
const ROW_HEIGHT = 24;

const TEXTAREA_VERTICAL_PADDING = 16;

const DEFAULT_ROWS = 4;

interface SurveyCommentQuestionProps {
  element: SurveyElement;
  control: Control<FieldValues>;
  errorMessage?: string;
  /**
   * Supporting copy rendered between the label and the input, so it is read in
   * the order it applies: what the question is, then what answering it does,
   * then the field itself.
   */
  description?: ReactNode;
  /** Id of supporting copy to announce as this field's description on web. */
  describedById?: string;
  /** Same supporting copy as text, since native announces a hint, not an id. */
  accessibilityHint?: string;
  hideRequiredMarker?: boolean;
  placeholder?: string;
  /** Gives the box the brand border, a white fill and a placeholder that clears AA on it. */
  emphasized?: boolean;
}

/**
 * Renders a `comment` element — a free-text answer that needs more room than a
 * single line. Sized from the schema's `rows`, which is presentation-only: the
 * server ignores it, so it is a hint about expected answer length rather than
 * any kind of limit.
 *
 * The label is set through both `aria-label` and `accessibilityLabel`, and both
 * are load-bearing. Gluestack's textarea destructures `aria-label` with its own
 * default of "Input Field", so passing only `accessibilityLabel` leaves that
 * placeholder as the accessible name on web; native reads `accessibilityLabel`.
 */
export function SurveyCommentQuestion({
  element,
  control,
  errorMessage,
  description,
  describedById,
  accessibilityHint,
  hideRequiredMarker,
  placeholder,
  emphasized,
}: SurveyCommentQuestionProps) {
  const minHeight = (element.rows ?? DEFAULT_ROWS) * ROW_HEIGHT + TEXTAREA_VERTICAL_PADDING;

  const accessibleLabel = element.isRequired ? `${element.title}, required` : element.title;

  return (
    <Controller
      name={element.name}
      control={control}
      render={({ field: { onChange, onBlur, value } }) => (
        <FormControl
          isInvalid={!!errorMessage}
          aria-required={element.isRequired}
          className="gap-2">
          <SurveyQuestionLabel element={element} hideRequiredMarker={hideRequiredMarker} />

          {description}

          <Textarea
            className={twMerge(TEXTAREA_HEIGHT_RESET, emphasized && EMPHASIZED_TEXTAREA_FRAME)}
            style={{ minHeight }}>
            <TextareaInput
              className={emphasized ? EMPHASIZED_PLACEHOLDER : undefined}
              testID={element.name}
              {...getSurveyProseKeyboardProps(true)}
              value={typeof value === 'string' ? value : ''}
              onChangeText={onChange}
              onBlur={onBlur}
              multiline
              placeholder={placeholder}
              textAlignVertical="top"
              aria-label={accessibleLabel}
              accessibilityLabel={accessibleLabel}
              inputAccessoryViewID={getSurveyAccessoryViewId(element.name)}
              aria-describedby={describedById}
              accessibilityHint={accessibilityHint}
            />
          </Textarea>

          <SurveyKeyboardDoneBar fieldName={element.name} />

          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} />
            <FormControlErrorText>{errorMessage}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
